import type { FormField, FormSchema } from './form-schema.js';

/**
 * The field types whose answer can decide which shares may share an animal. A
 * `select` or a `radio` stores one option value and a `checkbox` a tick, so two
 * answers are equal or they are not; a `multiselect` stores a set, and a set
 * has no single value two shares could agree on, so it is not offered.
 */
export const GROUPING_FIELD_TYPES = ['select', 'radio', 'checkbox'] as const;

export type GroupingFieldType = (typeof GROUPING_FIELD_TYPES)[number];

/**
 * How many of its own answers a campaign may make its shares agree on. Each
 * one more splits the queue into more runs, and a run shorter than an animal is
 * an animal left short, so a campaign needing more than this is describing
 * separate campaigns rather than one queue.
 */
export const MAX_GROUPING_FIELD_KEYS = 8;

export function isGroupingField(
  field: Pick<FormField, 'type'>,
): field is Extract<FormField, { type: GroupingFieldType }> {
  return (GROUPING_FIELD_TYPES as readonly string[]).includes(field.type);
}

/** The fields a campaign may name as grouping answers, in schema order. */
export function groupingFieldCandidates(
  schema: FormSchema,
): Extract<FormField, { type: GroupingFieldType }>[] {
  return schema.fields.filter(isGroupingField);
}

/** Why a campaign may not name these grouping keys against this published schema. */
export type GroupingFieldKeyProblem = {
  readonly problem: 'field_unknown' | 'field_not_groupable' | 'field_named_twice';
  readonly fieldKey: string;
};

/**
 * The rule the trigger `campaigns_group_keys_name_published_fields` holds,
 * asked first so a refusal names the key rather than a trigger: every key names
 * a published `select`, `radio` or `checkbox`, and none twice. It is asked when
 * the keys are set and when a schema is published, because either can break it
 * — a publish that drops a named field, or turns it into a text box, is the same
 * mistake arriving from the other side.
 */
export function groupingFieldKeyProblem(
  keys: readonly string[],
  schema: FormSchema,
): GroupingFieldKeyProblem | null {
  const seen = new Set<string>();
  for (const fieldKey of keys) {
    if (seen.has(fieldKey)) return { problem: 'field_named_twice', fieldKey };
    seen.add(fieldKey);
    const field = schema.fields.find((candidate) => candidate.key === fieldKey);
    if (!field) return { problem: 'field_unknown', fieldKey };
    if (!isGroupingField(field)) return { problem: 'field_not_groupable', fieldKey };
  }
  return null;
}

/**
 * One answer as grouping compares it. A `select` or `radio` answers with an
 * option value and a `checkbox` with a tick; an unanswered field, an empty
 * string and an unticked box are all *no*, because a share nobody ticked video
 * for and one somebody unticked are the same share on the slaughter floor.
 */
export type GroupingAnswer = string | number | true | null;

export function groupingAnswer(value: unknown): GroupingAnswer {
  if (value === true) return true;
  if (typeof value === 'string') return value === '' ? null : value;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

function answersOf(formData: unknown): Readonly<Record<string, unknown>> {
  if (formData === null || typeof formData !== 'object' || Array.isArray(formData)) return {};
  return formData as Record<string, unknown>;
}

/** A share's answers to the campaign's grouping keys, in the keys' order. */
export function groupingAnswers(
  formData: unknown,
  keys: readonly string[],
): readonly GroupingAnswer[] {
  const answers = answersOf(formData);
  return keys.map((key) => groupingAnswer(answers[key]));
}

/**
 * What the shares of one animal must agree on: the yurt the work is planned at
 * — `donations.fulfillment_institution_id`, a column and so never an answer —
 * and the campaign's own named answers. A campaign naming no answer has no key
 * at all, so its queue is the one window 0025 derived; naming any makes the
 * yurt decide as well, since shares of one animal are slaughtered in one place.
 *
 * `form_data` is untyped `jsonb` and arrives as `unknown`, read leniently like
 * every reader of a document captured under an older schema version.
 */
export function queueGroupKey(
  row: { readonly fulfillmentInstitutionId: string | null; readonly formData: unknown },
  keys: readonly string[],
): string | null {
  if (keys.length === 0) return null;
  return JSON.stringify([row.fulfillmentInstitutionId, ...groupingAnswers(row.formData, keys)]);
}

/**
 * The one grouping answer whose options carry their own group size, and the
 * sizes by option value — `campaigns.group_size_field_key` and `group_sizes`.
 * A campaign naming no field sizes every group by `units_per_group`.
 */
export type GroupSizing = {
  readonly fieldKey: string | null;
  readonly sizes: Readonly<Record<string, number>>;
};

export const NO_GROUP_SIZING: GroupSizing = { fieldKey: null, sizes: {} };

/** The field types whose answer can carry a group size: one option value per share. */
export const GROUP_SIZE_FIELD_TYPES = ['select', 'radio'] as const;

/**
 * How many shares make one animal for this share, when its answer names a
 * size; null when the campaign names no size field or the map does not name
 * this answer, which means the campaign's `units_per_group`. Read leniently,
 * like every reader of `form_data`.
 */
export function groupSizeOf(formData: unknown, sizing: GroupSizing): number | null {
  if (sizing.fieldKey === null) return null;
  const answer = groupingAnswer(answersOf(formData)[sizing.fieldKey]);
  if (typeof answer !== 'string') return null;
  const size = sizing.sizes[answer];
  return typeof size === 'number' && Number.isInteger(size) && size > 0 ? size : null;
}

export type GroupSizeProblem =
  | { readonly problem: 'field_unknown'; readonly fieldKey: string }
  | { readonly problem: 'field_not_grouped'; readonly fieldKey: string }
  | { readonly problem: 'option_unknown'; readonly fieldKey: string; readonly option: string };

/**
 * The rules the database holds for a group size field, asked first so a
 * refusal names the field and the option: it is a published `select` or
 * `radio`; it is one of the campaign's grouping keys and the campaign has a
 * `units_per_group` for the answers the map leaves out — so the shares of one
 * animal always agree on it, and no animal holds shares of two sizes; and every
 * sized answer is one of its options.
 */
export function groupSizeProblem(
  config: {
    readonly sizing: GroupSizing;
    readonly groupByFieldKeys: readonly string[];
    readonly unitsPerGroup: number | null;
  },
  schema: FormSchema,
): GroupSizeProblem | null {
  const { fieldKey, sizes } = config.sizing;
  if (fieldKey === null) return null;
  const field = schema.fields.find((candidate) => candidate.key === fieldKey);
  if (!field || !(GROUP_SIZE_FIELD_TYPES as readonly string[]).includes(field.type)) {
    return { problem: 'field_unknown', fieldKey };
  }
  if (!config.groupByFieldKeys.includes(fieldKey) || config.unitsPerGroup === null) {
    return { problem: 'field_not_grouped', fieldKey };
  }
  const options = 'options' in field ? field.options.map((option) => option.value) : [];
  const option = Object.keys(sizes).find((value) => !options.includes(value));
  return option === undefined ? null : { problem: 'option_unknown', fieldKey, option };
}
